import React, { useState, useEffect, useCallback } from 'react';
import {
    FileText, Search, Filter,
    File as LucideFile, FileSpreadsheet, FileImage, FolderOpen, X,
    FileEdit, Presentation, LayoutGrid, List, Eye, Calendar
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api, { API_BASE_URL } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';

interface AppDocument {
    id: number;
    title: string;
    description: string | null;
    category: string;
    brand: string;
    filename: string;
    file_size: number;
    mime_type: string;
    created_at: string;
    download_count: number;
    uploader_name: string | null;
    download_url: string | null;
}

interface Category {
    value: string;
    label: string;
}

const getFileIcon = (mimeType: string, filename: string): React.ElementType => {
    const ext = filename.split('.').pop()?.toLowerCase() || '';

    if (mimeType.includes('pdf') || ext === 'pdf') return FileText;

    // Excel / Spreadsheets
    if (mimeType.includes('spreadsheet') || mimeType.includes('excel') || ['xls', 'xlsx', 'csv', 'ods'].includes(ext)) {
        return FileSpreadsheet;
    }

    // PowerPoint / Presentations
    if (mimeType.includes('presentation') || mimeType.includes('powerpoint') || ['ppt', 'pptx', 'odp'].includes(ext)) {
        return Presentation;
    }

    // Word / Documents
    if (mimeType.includes('word') || mimeType.includes('officedocument.wordprocessingml') || ['doc', 'docx', 'odt', 'txt'].includes(ext)) {
        return FileEdit;
    }

    if (mimeType.includes('image')) return FileImage;

    return LucideFile;
};

export default function ClientDocumentation() {
    const [documents, setDocuments] = useState<AppDocument[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [documentUrl, setDocumentUrl] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string>('');
    const [viewingDocument, setViewingDocument] = useState<AppDocument | null>(null);
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
    const [sortConfig, setSortConfig] = useState<{ key: 'title' | 'category'; direction: 'asc' | 'desc' }>({ key: 'title', direction: 'asc' });

    const sortedDocuments = [...documents].sort((a, b) => {
        if (sortConfig.key === 'title') {
            return sortConfig.direction === 'asc'
                ? a.title.localeCompare(b.title, 'fr', { sensitivity: 'base' })
                : b.title.localeCompare(a.title, 'fr', { sensitivity: 'base' });
        }
        if (sortConfig.key === 'category') {
            const labelA = categories.find(c => c.value === a.category)?.label || a.category;
            const labelB = categories.find(c => c.value === b.category)?.label || b.category;
            return sortConfig.direction === 'asc'
                ? labelA.localeCompare(labelB, 'fr', { sensitivity: 'base' })
                : labelB.localeCompare(labelA, 'fr', { sensitivity: 'base' });
        }
        return 0;
    });

    const handleSort = (key: 'title' | 'category') => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const loadDocuments = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            if (selectedCategory) params.append('category', selectedCategory);
            if (searchQuery) params.append('search', searchQuery);
            // Ne pas envoyer le paramètre brand - le backend filtre automatiquement par la marque du client

            const response = await api.get(`/documents?${params.toString()}`);
            setDocuments(response.data);
        } catch (error: any) {
            console.error('Error loading documents:', error);
            toast.error('Erreur lors du chargement des documents');
        } finally {
            setLoading(false);
        }
    }, [selectedCategory, searchQuery]);

    const loadCategories = async () => {
        try {
            const response = await api.get('/documents/categories');
            setCategories(response.data);
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    };

    useEffect(() => {
        loadCategories();
    }, []);

    useEffect(() => {
        loadDocuments();
    }, [loadDocuments]);

    const handleView = (doc: AppDocument) => {
        setViewingDocument(doc);
        setDocumentUrl(null);
        setLoadingPreview(true);

        const fileExt = doc.filename.split('.').pop()?.toLowerCase() || '';
        const officeExtensions = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp'];
        const isOfficeFile = officeExtensions.includes(fileExt);
        const isPdf = fileExt === 'pdf' || doc.mime_type === 'application/pdf';
        const isImage = doc.mime_type.includes('image');

        const token = useAuthStore.getState().token;
        if ((isOfficeFile || isPdf || isImage) && token) {
            const url = `${API_BASE_URL}/documents/${doc.id}/preview-pdf?token=${encodeURIComponent(token)}`;
            setDocumentUrl(url);
        }
        setLoadingPreview(false);
    };

    const closeViewer = () => {
        setViewingDocument(null);
        setDocumentUrl(null);
    };

    return (
        <div className="px-4 py-4 sm:p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-4 sm:mb-6">
                <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                    Documentation
                </h1>
                <p className="text-base text-slate-500 dark:text-slate-400 mt-1">
                    Documents de formation, certificats et conformité
                </p>
            </div>

            {/* Filters */}
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-3 sm:p-4 mb-4 sm:mb-6">
                <div className="flex flex-col gap-3 sm:gap-4">
                    {/* Search row */}
                    <div className="flex flex-row gap-2 items-center">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Rechercher un document..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-9 sm:pl-10 pr-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                            />
                        </div>
                        {/* View switcher — always visible next to search */}
                        <div className="flex items-center bg-slate-100 dark:bg-slate-700 rounded-lg p-1 flex-shrink-0">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`p-2 rounded-md transition-all ${viewMode === 'grid'
                                    ? 'bg-white dark:bg-slate-600 text-purple-600 dark:text-purple-400 shadow-sm'
                                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                                    }`}
                                title="Vue grille"
                            >
                                <LayoutGrid className="h-4 w-4" />
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`p-2 rounded-md transition-all ${viewMode === 'list'
                                    ? 'bg-white dark:bg-slate-600 text-purple-600 dark:text-purple-400 shadow-sm'
                                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
                                    }`}
                                title="Vue liste"
                            >
                                <List className="h-4 w-4" />
                            </button>
                        </div>
                    </div>

                    {/* Filters row */}
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        <Filter className="h-4 w-4 sm:h-5 sm:w-5 text-slate-400 flex-shrink-0" />
                        <select
                            value={sortConfig.key}
                            onChange={(e) => handleSort(e.target.value as 'title' | 'category')}
                            className="flex-1 min-w-0 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                        >
                            <option value="title">Trier par Titre</option>
                            <option value="category">Trier par Catégorie</option>
                        </select>

                        <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            className="flex-1 min-w-0 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                        >
                            <option value="">Toutes les catégories</option>
                            {categories.map(cat => (
                                <option key={cat.value} value={cat.value}>{cat.label}</option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Documents Grid */}
            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                </div>
            ) : documents.length === 0 ? (
                <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-8 sm:p-12 text-center">
                    <FolderOpen className="h-16 w-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Aucun document
                    </h3>
                    <p className="text-slate-500 dark:text-slate-400">
                        {searchQuery || selectedCategory
                            ? 'Aucun document ne correspond à vos critères de recherche'
                            : 'Aucun document n\'a encore été mis à disposition'}
                    </p>
                </div>
            ) : viewMode === 'grid' ? (
                /* ========== VUE GRILLE ========== */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
                    {sortedDocuments.map(doc => {
                        const DocumentFileIcon = getFileIcon(doc.mime_type, doc.filename);
                        return (
                            <div
                                key={doc.id}
                                onClick={() => handleView(doc)}
                                className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 hover:shadow-lg hover:border-purple-300 dark:hover:border-purple-600 transition-all duration-200 cursor-pointer group flex flex-col h-full overflow-hidden"
                            >
                                {/* Header coloré avec icône */}
                                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 px-4 py-4 sm:px-5 border-b border-slate-100 dark:border-slate-700">
                                    <div className="flex items-start gap-4">
                                        <div className="p-3 bg-white dark:bg-slate-700 rounded-xl shadow-sm group-hover:shadow-md transition-shadow">
                                            <DocumentFileIcon className="h-7 w-7 text-purple-600 dark:text-purple-400" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-semibold text-slate-800 dark:text-slate-100 text-base leading-tight line-clamp-2" title={doc.title}>
                                                {doc.title}
                                            </h3>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-4 sm:p-5 flex flex-col flex-1">
                                    {/* Description */}
                                    {doc.description && (
                                        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 line-clamp-2">
                                            {doc.description}
                                        </p>
                                    )}

                                    {/* Catégorie */}
                                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400">
                                            {categories.find(c => c.value === doc.category)?.label || doc.category}
                                        </span>
                                    </div>

                                    {/* Date */}
                                    <div className="flex items-center gap-1.5 text-xs text-slate-400 dark:text-slate-500 mb-4">
                                        <Calendar className="h-3.5 w-3.5" />
                                        Ajouté le {new Date(doc.created_at).toLocaleDateString('fr-FR')}
                                    </div>

                                    {/* Actions */}
                                    <div className="flex items-center gap-2 mt-auto pt-3 border-t border-slate-100 dark:border-slate-700">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleView(doc);
                                            }}
                                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all text-sm font-medium shadow-sm"
                                        >
                                            <Eye className="h-4 w-4" />
                                            Visualiser
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                /* ========== VUE LISTE ========== */
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
                    {/* Header de la liste */}
                    <div className="hidden md:grid md:grid-cols-12 gap-4 px-6 py-3 bg-slate-50 dark:bg-slate-700/50 border-b border-slate-200 dark:border-slate-600 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                        <div
                            className="col-span-6 flex items-center gap-1 cursor-pointer hover:text-purple-600 transition-colors"
                            onClick={() => handleSort('title')}
                        >
                            Document
                            <div className="flex flex-col ml-1">
                                <span className={`leading-[0] ${sortConfig.key === 'title' && sortConfig.direction === 'asc' ? 'text-purple-500' : 'text-slate-300 dark:text-slate-600'}`}>▲</span>
                                <span className={`leading-[0] ${sortConfig.key === 'title' && sortConfig.direction === 'desc' ? 'text-purple-500' : 'text-slate-300 dark:text-slate-600'}`}>▼</span>
                            </div>
                        </div>
                        <div
                            className="col-span-3 flex items-center gap-1 cursor-pointer hover:text-purple-600 transition-colors"
                            onClick={() => handleSort('category')}
                        >
                            Catégorie
                            <div className="flex flex-col ml-1">
                                <span className={`leading-[0] ${sortConfig.key === 'category' && sortConfig.direction === 'asc' ? 'text-purple-500' : 'text-slate-300 dark:text-slate-600'}`}>▲</span>
                                <span className={`leading-[0] ${sortConfig.key === 'category' && sortConfig.direction === 'desc' ? 'text-purple-500' : 'text-slate-300 dark:text-slate-600'}`}>▼</span>
                            </div>
                        </div>
                        <div className="col-span-2">Date</div>
                        <div className="col-span-1 text-right">Action</div>
                    </div>

                    {/* Liste des documents */}
                    <div className="divide-y divide-slate-100 dark:divide-slate-700">
                        {sortedDocuments.map(doc => {
                            const DocumentFileIcon = getFileIcon(doc.mime_type, doc.filename);
                            return (
                                <div
                                    key={doc.id}
                                    onClick={() => handleView(doc)}
                                    className="grid grid-cols-1 md:grid-cols-12 gap-3 sm:gap-4 px-4 py-4 sm:px-6 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors cursor-pointer group items-center"
                                >
                                    {/* Document info */}
                                    <div className="md:col-span-6 flex items-center gap-4">
                                        <div className="p-2.5 bg-purple-100 dark:bg-purple-900/30 rounded-lg group-hover:bg-purple-200 dark:group-hover:bg-purple-800/50 transition-colors flex-shrink-0">
                                            <DocumentFileIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <h3 className="font-medium text-slate-800 dark:text-slate-100 line-clamp-2" title={doc.title}>
                                                {doc.title}
                                            </h3>
                                        </div>
                                    </div>

                                    {/* Catégorie */}
                                    <div className="md:col-span-3 text-sm text-slate-600 dark:text-slate-400">
                                        <span className="truncate">{categories.find(c => c.value === doc.category)?.label || doc.category}</span>
                                    </div>

                                    {/* Date */}
                                    <div className="md:col-span-2 text-sm text-slate-500 dark:text-slate-400">
                                        <div className="flex items-center gap-1.5">
                                            <Calendar className="h-3.5 w-3.5 flex-shrink-0" />
                                            {new Date(doc.created_at).toLocaleDateString('fr-FR')}
                                        </div>
                                    </div>

                                    {/* Actions */}
                                    <div className="md:col-span-1 flex items-center justify-end">
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleView(doc);
                                            }}
                                            className="p-2 text-purple-600 dark:text-purple-400 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-lg transition-colors"
                                            title="Visualiser"
                                        >
                                            <Eye className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Document Viewer Modal */}
            {viewingDocument && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-0 sm:p-4">
                    <div className="bg-white dark:bg-slate-800 w-full h-full sm:h-[90vh] sm:max-w-6xl sm:rounded-xl sm:shadow-xl flex flex-col">
                        <div className="flex items-center justify-between px-4 py-4 sm:px-6 sm:py-4 border-b border-slate-200 dark:border-slate-700">
                            <div className="min-w-0 flex-1">
                                <h2 className="text-base sm:text-lg font-semibold text-slate-800 dark:text-slate-100 truncate">
                                    {viewingDocument.title}
                                </h2>
                                <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                                    {viewingDocument.filename}
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={closeViewer}
                                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-2"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>
                        </div>

                        <div className="flex-1 overflow-hidden p-4">
                            {loadingPreview ? (
                                <div className="flex items-center justify-center h-full">
                                    <div className="text-center">
                                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                                        <p className="text-slate-600 dark:text-slate-400">Chargement du document...</p>
                                    </div>
                                </div>

                            ) : documentUrl ? (
                                    <iframe
                                        src={`${documentUrl}#view=Fit`}
                                        className="w-full h-full"
                                        style={{
                                            border: 'none',
                                            // Some mobile browsers respect zoom to force the document smaller
                                            zoom: '0.99',
                                            width: '100%',
                                        }}
                                    />
                            ) : (
                                <div className="flex items-center justify-center h-full">
                                    <div className="text-center p-8 bg-slate-50 dark:bg-slate-900/50 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700">
                                        <FileText className="h-16 w-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                                        <h3 className="text-lg font-medium text-slate-800 dark:text-slate-200 mb-2">
                                            Aperçu non disponible
                                        </h3>
                                        <p className="text-slate-600 dark:text-slate-400 max-w-sm">
                                            Ce fichier ne peut pas être visualisé directement dans le navigateur.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
